import React, { useRef, useState, useImperativeHandle, useEffect } from "react";
import { Box, Typography, Alert } from "@mui/material";
import * as Yup from "yup";
import FormBuilder from "../../../../shared/components/form-builder/form-builder";
import { AlternativeTransactionCertificateDetails } from "../../atc-models/atc-org";
import { StyledButton } from "../../../../shared/components/buttons/custom-button";
import useNotification from "../../../../shared/hooks/useNotification";
import { useTransactionCertificateStore } from "../../../../store/transaction-certificates.store";
import FileUploader from "../../../../shared/components/inputfields/atcFileUploader";

// ----------------- Constants -----------------
const UPLOAD_TYPE_OPTIONS = [
  { value: "GOTS", label: "GOTS" },
  { value: "ATU", label: "Alternative Transaction Upload" },
];

const TC_STANDARD_OPTIONS = [
  { label: "Organic Content Standard (OCS)", value: "OCS" },
  { label: "Responsible Down Standard (RDS)", value: "RDS" },
  { label: "Responsible Alpaca Standard (RAS)", value: "RAS" },
  { label: "Global Recycled Standard (GRS)", value: "GRS" },
  { label: "Responsible Wool Standard (RWS)", value: "RWS" },
  { label: "Content Claim Standard (CCS)", value: "CCS" },
  { label: "Recycled Claim Standard (RCS)", value: "RCS" },
  { label: "Responsible Mohair Standard (RMS)", value: "RMS" },
];

// ----------------- Props -----------------
interface TransactionCertificateDetailsFormProps {
  ref: React.RefObject<any>;
  detailsData: AlternativeTransactionCertificateDetails;
  setDetailsData: (data: AlternativeTransactionCertificateDetails) => void;
}

const TransactionCertificateDetailsForm = React.forwardRef<
  any,
  TransactionCertificateDetailsFormProps
>(({ detailsData, setDetailsData }, ref) => {
  const { cbLicensingCode } = useTransactionCertificateStore();
  const shortCode = cbLicensingCode?.replace(/^CB-/, "") || "";

  const formikRef = useRef<any>(null);
  const { notify } = useNotification();

  const [uploadedFile, setUploadedFile] = useState<File | null>(
    detailsData.uploadedFile instanceof File ? detailsData.uploadedFile : null
  );
  const [atcNo, setAtcNo] = useState("");
  const [atcVersionNo, setAtcVersionNo] = useState("");
  const [fixedPrefix, setFixedPrefix] = useState("");
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [isEditable, setIsEditable] = useState(false);

  // ----------------- Utils -----------------
  const updateFixedPrefix = (uploadType: string) => {
    if (uploadType && cbLicensingCode) {
      const shortCb = cbLicensingCode.replace(/^CB-/, "").toUpperCase();
      const newPrefix = `${uploadType}-${shortCb}-`;
      setFixedPrefix(newPrefix);

      // Initialize TC Number & Version Number if empty
      const currentTC = formikRef.current?.values.transactionCertificateNumber || "";
      if (!currentTC.startsWith(newPrefix)) {
        formikRef.current?.setFieldValue("transactionCertificateNumber", newPrefix);
        formikRef.current?.setFieldValue("tcVersionNumber", newPrefix);
        setAtcNo(newPrefix);
        setAtcVersionNo(newPrefix);
      }
    } else {
      setFixedPrefix("");
    }
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const uploadType = formikRef.current?.values?.uploadType;
      if (uploadType === "GOTS" || uploadType === "ATU") {
        setIsEditable(true);
      } else {
        setIsEditable(false);
      }
    }, 100);
  
    return () => clearInterval(interval);
  }, []);
  

  useEffect(() => {
    if (detailsData.uploadType && cbLicensingCode) {
      const shortCb = cbLicensingCode.replace(/^CB-/, "").toUpperCase();
      const newPrefix = `${detailsData.uploadType}-${shortCb}-`;
      setFixedPrefix(newPrefix);

      const currentTC = formikRef.current?.values.transactionCertificateNumber || "";
      if (!currentTC.startsWith(newPrefix)) {
        formikRef.current?.setFieldValue("transactionCertificateNumber", newPrefix);
        formikRef.current?.setFieldValue("tcVersionNumber", newPrefix);
        setAtcNo(newPrefix);
        setAtcVersionNo(newPrefix);
      } else {
        setAtcNo(currentTC);
        setAtcVersionNo(formikRef.current?.values.tcVersionNumber || newPrefix);
      }
    }
  }, [detailsData.uploadType, cbLicensingCode]);

  // ----------------- Validation Schema -----------------
  const validationSchema = Yup.object().shape({
    uploadType: Yup.string().required("Upload Type is required"),
    cbLicensingCode: Yup.string().required("CB Licensing Code is required"),
    transactionCertificateNumber: Yup.string()
      .required("Transaction Certificate Number is required")
      .test(
        "has-suffix",
        "Must contain input after the prefix",
        (value) => !!value && value.length > fixedPrefix.length
      ),
    dateOfIssue: Yup.string().required("Date of Issue is required"),
    tcStandard: Yup.array()
      .min(1, "Select at least one TC Standard")
      .required("TC Standard is required"),
    tcVersionNumber: Yup.string()
      .required("TC Version Number is required")
      .test(
        "has-suffix",
        "Must contain input after the prefix",
        (value) => !!value && value.length > fixedPrefix.length
      ),
    uploadedFile: Yup.mixed<File>().required("Upload File is Required"),
  });

  // ----------------- File Upload -----------------
  const isFileUploadDisabled = !(
    formikRef.current?.values.transactionCertificateNumber &&
    formikRef.current?.values.tcVersionNumber
  );

  const handleUploadSuccess = (fileInfo: { fileName: string; file: File }) => {
    setUploadedFile(fileInfo.file);
    formikRef.current?.setFieldValue("uploadedFile", fileInfo.file);
    setUploadStatus('success');
    notify(`File ${fileInfo.fileName} uploaded successfully (${(fileInfo.file.size / 1024).toFixed(2)} KB)`, "success");
  };

  const handleUploadError = (error: string) => {
    setUploadStatus('error');
    setErrorDetails(error);
    notify(`Upload failed: ${error}`, "error");
  };

  const resetUploader = () => {
    setUploadStatus('idle');
    setErrorDetails(null);
    setUploadedFile(null);
    formikRef.current?.setFieldValue("uploadedFile", null);
  };

  // ----------------- Form Config -----------------
  const formConfig = {
    pageName: "transaction-certificate",
    initialValues: { ...detailsData },
    validationSchema,
    enableReinitialize: true,
    sections: [
      {
        fields: [
          {
            label: "Upload Type",
            name: "uploadType",
            type: "select",
            required: true,
            gridSize: { xs: 12, md: 6, lg: 6 },
            props: {
              placeholder: "Select Upload Type",
              options: UPLOAD_TYPE_OPTIONS,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                formikRef.current?.setFieldValue("uploadType", value);
                updateFixedPrefix(value);
              },
            },
          },
          {
            label: "CB Licensing Code",
            name: "cbLicensingCode",
            type: "text",
            required: true,
            gridSize: { xs: 12, md: 6, lg: 6 },
            props: {
              placeholder: "Enter CB Licensing Code",
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value.toUpperCase();
                formikRef.current?.setFieldValue("cbLicensingCode", value);
              },
            },
          },
          {
            label: "Transaction Certificate Number",
            name: "transactionCertificateNumber",
            type: "text",
            required: true,
            gridSize: { xs: 12, md: 6, lg: 6 },
            props: {
              placeholder: "Enter Transaction Certificate No",
              disabled: !isEditable,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                const userInput = e.target.value.toUpperCase();
                const newValue = userInput.startsWith(fixedPrefix)
                  ? userInput
                  : fixedPrefix + userInput.slice(fixedPrefix.length);
                formikRef.current?.setFieldValue("transactionCertificateNumber", newValue);

                const currentVersion = formikRef.current?.values.tcVersionNumber;
                if (!currentVersion || currentVersion === e.target.defaultValue) {
                  formikRef.current?.setFieldValue("tcVersionNumber", newValue);
                }

                setAtcNo(newValue);
              },
            },
          },
          {
            label: "Date of Issue",
            name: "dateOfIssue",
            type: "date",
            required: true,
            gridSize: { xs: 12, md: 6, lg: 6 },
            props: { placeholder: "Enter TC issue date" },
          },
          {
            label: "Standard",
            name: "tcStandard",
            type: "select",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            props: {
              multiple: true,
              enableSearch: true,
              options: TC_STANDARD_OPTIONS,
              placeholder: "Select one or more options",
            },
          },
          {
            label: "TC Version Number",
            name: "tcVersionNumber",
            type: "text",
            required: true,
            gridSize: { xs: 12, md: 6, lg: 6 },
            props: {
              placeholder: "Enter TC Version Number",
              disabled: !isEditable,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                const userInput = e.target.value.toUpperCase();
                const newValue = userInput.startsWith(fixedPrefix)
                  ? userInput
                  : fixedPrefix + userInput.slice(fixedPrefix.length);
                formikRef.current?.setFieldValue("tcVersionNumber", newValue);
                setAtcVersionNo(newValue);
              },
            },
          },
        ],
      },
    ],
    handleSubmit: (values: any) => {
      setDetailsData(values);
    },
  };

  // ----------------- Ref Methods -----------------
  const validateForm = async () => {
    const errors = await formikRef.current?.validateForm() ?? {};
    const tcNo = formikRef.current?.values.transactionCertificateNumber || "";
    const tcVersion = formikRef.current?.values.tcVersionNumber || "";
    if (tcNo.length <= fixedPrefix.length) {
      errors.transactionCertificateNumber = "Must contain input after the prefix";
    }
    if (tcVersion.length <= fixedPrefix.length) {
      errors.tcVersionNumber = "Must contain input after the prefix";
    }
    return errors;
  };

  const submitForm = async () => formikRef.current?.submitForm();

  useImperativeHandle(ref, () => ({
    validateForm,
    submitForm,
    values: formikRef.current?.values,
    isUploadComplete: Boolean(uploadedFile) &&
      formikRef.current?.values.transactionCertificateNumber.length > fixedPrefix.length &&
      formikRef.current?.values.tcVersionNumber.length > fixedPrefix.length,
  }));

  // ----------------- Render -----------------
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 4, fontWeight: "bold" }}>
        Alternative Transaction Details
      </Typography>

      <FormBuilder formConfig={formConfig} formRef={formikRef} />

      {/* File Upload */}
      <Box sx={{ mt: 3, maxWidth: "600px", margin: "0 auto" }}>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Attach Your Transaction Certificate (PDF) file <span style={{ color: 'red' }}>*</span>
          <br />
          Click “Upload File” to save your document.
        </Typography>

        {uploadStatus === 'error' && errorDetails && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorDetails}
          </Alert>
        )}

        <FileUploader
          uniqueId="transaction-certificate-upload"
          setFileName={() => {}}
          setFilePath={() => {}}
          onUploadSuccess={handleUploadSuccess}
          onUploadError={handleUploadError}
          atcNo={atcNo}
          atcVersionNo={atcVersionNo}
          disabled={isFileUploadDisabled}
          uploadedFile={uploadedFile}
        />
      </Box>
    </Box>
  );
});

export default TransactionCertificateDetailsForm;
